import { useState, useEffect, useMemo } from 'react';
import { useAvailableViewport } from './useHeaderHeight';

/**
 * نظام التحكم الذكي في الأحجام والتباعدات بناءً على المساحة المتاحة
 */
export function useSmartResponsive() {
  const { availableHeight, viewportHeight, headerHeight } = useAvailableViewport();
  const [screenWidth, setScreenWidth] = useState(0);

  useEffect(() => {
    const updateScreenWidth = () => {
      setScreenWidth(window.innerWidth);
    };

    updateScreenWidth();
    window.addEventListener('resize', updateScreenWidth);
    return () => window.removeEventListener('resize', updateScreenWidth);
  }, []);

  const responsiveConfig = useMemo(() => {
    // تحديد نوع الجهاز
    const isMobile = screenWidth < 768;
    const isTablet = screenWidth >= 768 && screenWidth < 1024;
    const isSmallMobile = screenWidth < 400;
    const isVeryShortScreen = availableHeight < 500;
    const isShortScreen = availableHeight < 600;

    // حساب نسبة التدرج بناءً على المساحة المتاحة
    const heightScale = Math.min(1, Math.max(0.6, availableHeight / 700));
    const widthScale = Math.min(1, Math.max(0.8, screenWidth / 380));
    const combinedScale = Math.min(heightScale, widthScale);

    // أحجام النصوص المتجاوبة
    const textSizes = {
      // العناوين الرئيسية
      hero: {
        mobile: isVeryShortScreen 
          ? `${Math.max(1.25, 1.75 * combinedScale)}rem`
          : isShortScreen 
          ? `${Math.max(1.5, 2 * combinedScale)}rem`
          : `${Math.max(1.75, 2.25 * combinedScale)}rem`,
        tablet: `${Math.max(2, 2.5 * heightScale)}rem`,
        desktop: `${Math.max(2.5, 3 * heightScale)}rem`
      },
      
      // العناوين الثانوية
      heading: {
        mobile: isVeryShortScreen 
          ? `${Math.max(1.1, 1.4 * combinedScale)}rem`
          : `${Math.max(1.25, 1.5 * combinedScale)}rem`,
        tablet: `${Math.max(1.5, 1.8 * heightScale)}rem`,
        desktop: `${Math.max(1.75, 2 * heightScale)}rem`
      },
      
      // النصوص العادية
      body: {
        mobile: isVeryShortScreen 
          ? `${Math.max(0.8, 0.9 * combinedScale)}rem`
          : `${Math.max(0.875, 1 * combinedScale)}rem`,
        tablet: `${Math.max(0.95, 1.1 * heightScale)}rem`,
        desktop: `${Math.max(1, 1.125 * heightScale)}rem`
      },
      
      // النصوص الصغيرة
      small: {
        mobile: `${Math.max(0.75, 0.8 * combinedScale)}rem`,
        tablet: `${Math.max(0.8, 0.9 * heightScale)}rem`,
        desktop: `${Math.max(0.875, 1 * heightScale)}rem`
      }
    };

    // التباعدات المتجاوبة
    const spacing = {
      // التباعدات الداخلية للحاويات
      container: {
        mobile: isVeryShortScreen ? '0.75rem' : isShortScreen ? '1rem' : '1.25rem',
        tablet: '1.5rem',
        desktop: '2rem'
      },
      
      // التباعدات بين العناصر
      elements: {
        mobile: isVeryShortScreen ? '0.5rem' : isShortScreen ? '0.75rem' : '1rem',
        tablet: '1.25rem',
        desktop: '1.5rem'
      },
      
      // التباعدات بين الأقسام
      sections: {
        mobile: isVeryShortScreen ? '1rem' : isShortScreen ? '1.5rem' : '2rem',
        tablet: '3rem',
        desktop: '4rem'
      }
    };

    // أبعاد الكاروسيل المتجاوبة
    const carouselDimensions = {
      container: {
        height: isMobile 
          ? `${Math.max(300, availableHeight * 0.55)}px`
          : `${Math.max(400, availableHeight * 0.65)}px`
      },
      
      card: {
        mobile: {
          width: `${Math.min(350, screenWidth * 0.9)}px`,
          height: isVeryShortScreen 
            ? `${Math.max(250, availableHeight * 0.4)}px`
            : `${Math.max(300, availableHeight * 0.45)}px`
        },
        center: {
          width: `${Math.min(420, availableHeight * 0.6)}px`,
          height: `${Math.max(320, availableHeight * 0.45)}px`
        },
        side: {
          width: `${Math.min(360, availableHeight * 0.52)}px`,
          height: `${Math.max(280, availableHeight * 0.4)}px`
        }
      }
    };

    return {
      // معلومات الجهاز
      device: {
        isMobile,
        isTablet,
        isSmallMobile,
        isVeryShortScreen,
        isShortScreen
      },
      
      // معاملات التدرج
      scales: {
        heightScale,
        widthScale,
        combinedScale
      },
      
      // الأحجام
      textSizes,
      spacing,
      carouselDimensions,
      
      // قيم CSS مفيدة
      cssVars: {
        '--available-height': `${availableHeight}px`,
        '--header-height': `${headerHeight}px`,
        '--viewport-height': `${viewportHeight}px`,
        '--screen-width': `${screenWidth}px`,
        '--combined-scale': combinedScale.toString(),
        '--height-scale': heightScale.toString(),
        '--width-scale': widthScale.toString()
      }
    };
  }, [availableHeight, viewportHeight, headerHeight, screenWidth]);

  return responsiveConfig;
}

/**
 * Hook مساعد لتطبيق الأنماط المتجاوبة
 */
export function useResponsiveStyles() {
  const config = useSmartResponsive();
  
  const getTextSize = (variant: 'hero' | 'heading' | 'body' | 'small') => {
    const { device, textSizes } = config;
    
    if (device.isMobile) return textSizes[variant].mobile;
    if (device.isTablet) return textSizes[variant].tablet;
    return textSizes[variant].desktop;
  };
  
  const getSpacing = (type: 'container' | 'elements' | 'sections') => {
    const { device, spacing } = config;
    
    if (device.isMobile) return spacing[type].mobile;
    if (device.isTablet) return spacing[type].tablet;
    return spacing[type].desktop;
  };
  
  const screenWidth = config.cssVars['--screen-width'] ? parseInt(config.cssVars['--screen-width']) : (typeof window !== 'undefined' ? window.innerWidth : 360);
  const availableHeight = config.cssVars['--available-height'] ? parseInt(config.cssVars['--available-height']) : 600;
  const headerHeight = config.cssVars['--header-height'] ? parseInt(config.cssVars['--header-height']) : 64;
  const mobileSquareSize = Math.min(screenWidth * 0.9, availableHeight * 0.6);

  return {
    ...config,
    getTextSize,
    getSpacing,
    // قيم إضافية للاستخدام المباشر
    headerHeight,
    availableHeight,
    screenWidth,
    mobileSquareSize,
  };
}